/**
 * Color Registry - Single Source of Truth for all colors
 *
 * Imported from: Brand Color List.xlsx
 * Total: 64 brand colors (33 Pet Health + 31 Farm Animal)
 * All colors are available on all 4 towers (pet, vet, farm, corporate)
 *
 * Each color has:
 * - id: Unique identifier
 * - name: Human-readable name
 * - hexCode: Hex color code
 * - lightHexCode: Optional light variant hex code
 * - aliases: Alternative names for backward compatibility
 * - brands: Brand names that use this color
 * - towers: Which towers can use this color
 * - customStyles: Optional custom Tailwind classes
 */

export interface ColorInfo {
	id: string
	name: string
	hexCode: string
	/** Light variant hex code (for backgrounds) - use 'default' or specific hex */
	lightHexCode?: string
	/**
	 * Text contrast for accessibility when this color is used as background.
	 * - 'light': Use white text (for dark backgrounds)
	 * - 'dark': Use black text (for light backgrounds)
	 * Defaults to 'light' if not specified.
	 */
	textContrast?: 'light' | 'dark'
	aliases: string[]
	/** Brand names that use this color (e.g., 'seresto', 'advantage') */
	brands: string[]
	towers: {
		pet: boolean
		vet: boolean
		farm: boolean
		corporate: boolean
	}
	/** Optional custom Tailwind classes for special styling */
	customStyles?: {
		/** Default button/element style */
		defaultStyle?: string
		/** Style when element is collapsed */
		collapsed?: string
		/** Style when element is selected */
		selected?: string
	}
}

type ColorRegistry = Record<string, ColorInfo>

/**
 * Color Registry - Single Source of Truth
 * Merged from Brand Color List.xlsx (64 primary) + legacy brand-colors.ts (289 additional)
 * Total: 353 brand colors (all available on all 4 towers)
 * Excel colors take priority for any duplicates
 */
export const colorRegistry: ColorRegistry = {
	// ========================================================================
	// US PET HEALTH BRANDS
	// ========================================================================
	advantage_multi: {
		id: 'advantage_multi',
		name: 'Advantage Multi',
		hexCode: '#f18e00',
		aliases: [],
		brands: ['Advantage Multi'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	advantage_ii: {
		id: 'advantage_ii',
		name: 'Advantage II',
		hexCode: '#007040',
		aliases: ['advatage_ii'],
		brands: ['Advantage II'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	atopica: {
		id: 'atopica',
		name: 'Atopica',
		hexCode: '#25076a',
		aliases: [],
		brands: ['Atopica'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	baytril: {
		id: 'baytril',
		name: 'Baytril',
		hexCode: '#c52e5a',
		aliases: [],
		brands: ['Baytril'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	bexacat: {
		id: 'bexacat',
		name: 'Bexacat',
		hexCode: '#CC27B0',
		textContrast: 'dark',
		aliases: [],
		brands: ['Bexacat'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	canine_parvovirus: {
		id: 'canine_parvovirus',
		name: 'Canine Parvovirus Monoclonal Antibody',
		hexCode: '#3565d5',
		aliases: ['canine_parvovirus_moloclonal_antibody'],
		brands: ['Canine Parvovirus Monoclonal Antibody'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	claro: {
		id: 'claro',
		name: 'Claro',
		hexCode: '#510c76',
		aliases: [],
		brands: ['Claro'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	comfortis: {
		id: 'comfortis',
		name: 'Comfortis',
		hexCode: '#f03127',
		aliases: [],
		brands: ['Comfortis'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	credelio: {
		id: 'credelio',
		name: 'Credelio',
		hexCode: '#00843D',
		aliases: [],
		brands: ['Credelio'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	credelio_quattro: {
		id: 'credelio_quattro',
		name: 'Credelio Quattro',
		hexCode: '#00643c',
		aliases: [],
		brands: ['Credelio Quattro'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	deramaxx: {
		id: 'deramaxx',
		name: 'Deramaxx',
		hexCode: '#0048e0',
		aliases: [],
		brands: ['Deramaxx'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	droncit: {
		id: 'droncit',
		name: 'Droncit',
		hexCode: '#3c5896',
		aliases: [],
		brands: ['Droncit'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	elura: {
		id: 'elura',
		name: 'Elura',
		hexCode: '#ab1369',
		aliases: [],
		brands: ['Elura'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	entyce: {
		id: 'entyce',
		name: 'Entyce',
		hexCode: '#440099',
		aliases: [],
		brands: ['Entyce'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	galliprant: {
		id: 'galliprant',
		name: 'Galliprant',
		hexCode: '#bf0d3e',
		aliases: [],
		brands: ['Galliprant'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	interceptor: {
		id: 'interceptor',
		name: 'Interceptor',
		hexCode: '#003da5',
		aliases: [],
		brands: ['Interceptor'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	interceptor_plus: {
		id: 'interceptor_plus',
		name: 'Interceptor Plus',
		hexCode: '#ffdd3d',
		// 'interceptorplus' is the legacy CMS dropdown codename (no underscore)
		aliases: ['interceptorplus'],
		brands: ['Interceptor Plus'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	k9_advantix_ii: {
		id: 'k9_advantix_ii',
		name: 'K9 Advantix II',
		hexCode: '#b20838',
		aliases: [],
		brands: ['K9 Advantix II'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	nocita: {
		id: 'nocita',
		name: 'Nocita',
		hexCode: '#0097a0',
		aliases: [],
		brands: ['Nocita'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	onsior: {
		id: 'onsior',
		name: 'Onsior',
		hexCode: '#00a9ce',
		aliases: [],
		brands: ['Onsior'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	percorten_v: {
		id: 'percorten_v',
		name: 'Percorten-V',
		hexCode: '#007663',
		aliases: ['profender'],
		brands: ['Percorten-V', 'Profender'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	pet_protect: {
		id: 'pet_protect',
		name: 'Pet Protect',
		hexCode: '#003057',
		aliases: [],
		brands: ['Pet Protect'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	rabvac: {
		id: 'rabvac',
		name: 'Rabvac',
		hexCode: '#004b8d',
		aliases: [],
		brands: ['Rabvac'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	seresto: {
		id: 'seresto',
		name: 'Seresto',
		hexCode: '#00597c',
		aliases: [],
		brands: ['Seresto'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	surolan: {
		id: 'surolan',
		name: 'Surolan',
		hexCode: '#8cc63f',
		aliases: [],
		brands: ['Surolan'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	tanovea: {
		id: 'tanovea',
		name: 'Tanovea',
		hexCode: '#0085ad',
		aliases: [],
		brands: ['Tanovea'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	trifexis: {
		id: 'trifexis',
		name: 'Trifexis',
		hexCode: '#a8ad00',
		aliases: [],
		brands: ['Trifexis'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	trucan: {
		id: 'trucan',
		name: 'TruCan',
		hexCode: '#1a4278',
		aliases: ['trufel'],
		brands: ['TruCan', 'TruFel'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	vetspon: {
		id: 'vetspon',
		name: 'Vetspon',
		hexCode: '#0073B6',
		aliases: [],
		brands: ['Vetspon'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	varenzin: {
		id: 'varenzin',
		name: 'Varenzin',
		hexCode: '#c41d4a',
		aliases: [],
		brands: ['Varenzin'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	veraflox: {
		id: 'veraflox',
		name: 'Veraflox',
		hexCode: '#281e69',
		aliases: [],
		brands: ['Veraflox'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	zenrelia: {
		id: 'zenrelia',
		name: 'Zenrelia',
		hexCode: '#215447',
		aliases: [],
		brands: ['Zenrelia'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
		customStyles: {
			defaultStyle: 'bg-[#215447] text-white hover:bg-[#74a30e]',
			collapsed: 'bg-[#215447] text-white',
			selected: 'bg-[#91cc12] text-white',
		},
	},
	zorbium: {
		id: 'zorbium',
		name: 'Zorbium',
		hexCode: '#ff64b2',
		aliases: [],
		brands: ['Zorbium'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},

	// ========================================================================
	// US FARM ANIMAL BRANDS
	// ========================================================================
	agita: {
		id: 'agita',
		name: 'Agita',
		hexCode: '#7ea0c4',
		aliases: [],
		brands: ['Agita'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	assist: {
		id: 'assist',
		name: 'Assist+',
		hexCode: '#859100',
		aliases: [],
		brands: ['Assist+'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	baseline: {
		id: 'baseline',
		name: 'Baseline',
		hexCode: '#db3168',
		aliases: [],
		brands: ['Baseline'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	beetleshield: {
		id: 'beetleshield',
		name: 'BeetleShield 6',
		hexCode: '#82c44a',
		aliases: ['beetleshield_6'],
		brands: ['BeetleShield 6'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	betasolve: {
		id: 'betasolve',
		name: 'Betasolve',
		hexCode: '#47aa42',
		aliases: [],
		brands: ['Betasolve'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	clean_up: {
		id: 'clean_up',
		name: 'Clean-Up II Insecticide',
		hexCode: '#78c046',
		aliases: [
			'clean_up_ii_insecticide',
			'permectrin',
			'quickbayt',
			'rabon',
		],
		brands: ['Clean-Up II', 'Permectrin CDS', 'Quickbayt', 'Rabon 50 WP'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	clinacox: {
		id: 'clinacox',
		name: 'Clinacox',
		hexCode: '#e5e1e5',
		textContrast: 'dark',
		aliases: [],
		brands: ['Clinacox'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	coban: {
		id: 'coban',
		name: 'Coban',
		hexCode: '#ffd200',
		textContrast: 'dark',
		aliases: [],
		brands: ['Coban'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	conquest: {
		id: 'conquest',
		name: 'Conquest',
		hexCode: '#f29e54',
		textContrast: 'dark',
		aliases: [],
		brands: ['Conquest'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	cylence_ultra: {
		id: 'cylence_ultra',
		name: 'Cylence Ultra',
		hexCode: '#48a350',
		aliases: [],
		brands: ['Cylence Ultra'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	dairy_relieve: {
		id: 'dairy_relieve',
		name: 'Dairy Relieve',
		hexCode: '#7b92c4',
		aliases: [],
		brands: ['Dairy Relieve'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	denagard: {
		id: 'denagard',
		name: 'Denagard',
		hexCode: '#1d3d7c',
		aliases: [],
		brands: ['Denagard'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	elector: {
		id: 'elector',
		name: 'Elector PSP',
		hexCode: '#e7ad36',
		textContrast: 'dark',
		aliases: ['elector_psp'],
		brands: ['Elector PSP'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	evosure: {
		id: 'evosure',
		name: 'Evosure',
		hexCode: '#e56606',
		aliases: [],
		brands: ['Evosure'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	feed_aid: {
		id: 'feed_aid',
		name: 'Feed Aid',
		hexCode: '#c48e32',
		aliases: [],
		brands: ['Feed Aid'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	hemicell: {
		id: 'hemicell',
		name: 'Hemicell',
		hexCode: '#30b19d',
		aliases: [],
		brands: ['Hemicell'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	increxxa: {
		id: 'increxxa',
		name: 'Increxxa',
		hexCode: '#4c12a1',
		aliases: [],
		brands: ['Increxxa'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	inteprity: {
		id: 'inteprity',
		name: 'Inteprity',
		hexCode: '#b25d27',
		aliases: ['kavault', 'surmax'],
		brands: ['Inteprity', 'Kavault', 'SURMAX'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	larvadex: {
		id: 'larvadex',
		name: 'Larvadex',
		hexCode: '#9f4879',
		aliases: [],
		brands: ['Larvadex'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	lipinate: {
		id: 'lipinate',
		name: 'Lipinate',
		hexCode: '#464c3e',
		aliases: [],
		brands: ['Lipinate'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	maxiban: {
		id: 'maxiban',
		name: 'Maxiban',
		hexCode: '#d31145',
		aliases: [],
		brands: ['Maxiban'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	monteban: {
		id: 'monteban',
		name: 'Monteban',
		hexCode: '#009b7a',
		aliases: [],
		brands: ['Monteban'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	neporex: {
		id: 'neporex',
		name: 'Neporex',
		hexCode: '#618376',
		aliases: [],
		brands: ['Neporex'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	pradalex: {
		id: 'pradalex',
		name: 'Pradalex',
		hexCode: '#26d07c',
		aliases: ['pradalex_pradofloxacin_injection'],
		brands: ['Pradalex'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	prevacent: {
		id: 'prevacent',
		name: 'Prevacent PRRS',
		hexCode: '#c7282e',
		aliases: ['prevacent_prrs'],
		brands: ['Prevacent PRRS'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	proteck: {
		id: 'proteck',
		name: 'Proteck Nursery',
		hexCode: '#cc9000',
		aliases: ['proteck_nursery'],
		brands: ['Proteck Nursery'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	rumensin: {
		id: 'rumensin',
		name: 'Rumensin',
		hexCode: '#f58426',
		textContrast: 'dark',
		aliases: [],
		brands: ['Rumensin'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	swine_awake: {
		id: 'swine_awake',
		name: 'Swine Awake',
		hexCode: '#7b2379',
		aliases: [],
		brands: ['Swine Awake'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	tylan: {
		id: 'tylan',
		name: 'TYLAN',
		hexCode: '#0093d0',
		aliases: [],
		brands: ['TYLAN'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	vapona: {
		id: 'vapona',
		name: 'Vapona',
		hexCode: '#74bd3d',
		aliases: [],
		brands: ['Vapona'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	zoashield: {
		id: 'zoashield',
		name: 'Zoashield',
		hexCode: '#092340',
		aliases: [],
		brands: ['Zoashield'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},

	// ========================================================================
	// LEGACY COLORS (imported from brand-colors.ts for backward compatibility)
	// ========================================================================
	acata: {
		id: 'acata',
		name: 'Acata',
		hexCode: '#509e2f',
		aliases: [],
		brands: ['Acata'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	adtab_black: {
		id: 'adtab_black',
		name: 'Adtab Black',
		hexCode: '#1d1d1b',
		aliases: [],
		brands: ['Adtab Black'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	adtab_white: {
		id: 'adtab_white',
		name: 'Adtab White',
		hexCode: '#ffffff',
		textContrast: 'dark',
		aliases: [],
		brands: ['Adtab White'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	adtab_yellow: {
		id: 'adtab_yellow',
		name: 'Adtab Yellow',
		hexCode: '#ffd400',
		textContrast: 'dark',
		aliases: [],
		brands: ['Adtab Yellow'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	advantage: {
		id: 'advantage',
		name: 'Advantage',
		hexCode: '#007040',
		lightHexCode: '#e5f0eb',
		aliases: [],
		brands: ['Advantage'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	advantage_ii_for_cats: {
		id: 'advantage_ii_for_cats',
		name: 'Advantage II for Cats',
		hexCode: '#007040',
		lightHexCode: '#e5f0eb',
		aliases: [],
		brands: ['Advantage II for Cats'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	advantage_ii_for_dogs: {
		id: 'advantage_ii_for_dogs',
		name: 'Advantage II for Dogs',
		hexCode: '#007040',
		lightHexCode: '#e5f0eb',
		aliases: [],
		brands: ['Advantage II for Dogs'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	advantage_multi_advocate: {
		id: 'advantage_multi_advocate',
		name: 'Advantage Multi (Advocate)',
		hexCode: '#f18e00',
		textContrast: 'dark',
		aliases: [],
		brands: ['Advantage Multi (Advocate)'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	advantage_multi_for_cats: {
		id: 'advantage_multi_for_cats',
		name: 'Advantage Multi for Cats',
		hexCode: '#f18e00',
		textContrast: 'dark',
		aliases: [],
		brands: ['Advantage Multi for Cats'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	advantage_multi_for_dogs: {
		id: 'advantage_multi_for_dogs',
		name: 'Advantage Multi for Dogs',
		hexCode: '#f18e00',
		textContrast: 'dark',
		aliases: [],
		brands: ['Advantage Multi for Dogs'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	advantage_max_3: {
		id: 'advantage_max_3',
		name: 'Advantage Max 3',
		hexCode: '#a7042a',
		aliases: [],
		brands: ['Advantage Max 3'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	advanthome: {
		id: 'advanthome',
		name: 'Advanthome',
		hexCode: '#b10431',
		lightHexCode: '#f7e5ea',
		aliases: [],
		brands: ['Advanthome'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	advantix: {
		id: 'advantix',
		name: 'Advantix',
		hexCode: '#b10431',
		lightHexCode: '#f9e5eb',
		aliases: [],
		brands: ['Advantix'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	onsior_advantix_light: {
		id: 'onsior_advantix_light',
		name: 'Onsior Advantix Light',
		hexCode: '#f9e5eb',
		textContrast: 'dark',
		aliases: [],
		brands: ['Onsior Advantix Light'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	advantus: {
		id: 'advantus',
		name: 'Advantus',
		hexCode: '#008752',
		lightHexCode: '#e5f3ed',
		aliases: [],
		brands: ['Advantus'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	advocate: {
		id: 'advocate',
		name: 'Advocate',
		hexCode: '#EF7D00',
		lightHexCode: '#EF7D00',
		textContrast: 'light',
		aliases: [],
		brands: ['Advocate'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	advocate_orange: {
		id: 'advocate_orange',
		name: 'Advocate Orange',
		hexCode: '#E57200',
		lightHexCode: '#E57200',
		textContrast: 'light',
		aliases: [],
		brands: ['Advocate Orange'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	agita_10g: {
		id: 'agita_10g',
		name: 'Agita 10G',
		hexCode: '#7ea0c4',
		aliases: [],
		brands: ['Agita 10G'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	agita_10wg: {
		id: 'agita_10wg',
		name: 'Agita 10WG',
		hexCode: '#7ea0c4',
		aliases: [],
		brands: ['Agita 10WG'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	agita_flybait: {
		id: 'agita_flybait',
		name: 'Agita Flybait',
		hexCode: '#7ea0c4',
		aliases: [],
		brands: ['Agita Flybait'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	agrovet: {
		id: 'agrovet',
		name: 'Agrovet',
		hexCode: '#0476b0',
		aliases: [],
		brands: ['Agrovet'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	alenza: {
		id: 'alenza',
		name: 'Alenza',
		hexCode: '#249390',
		lightHexCode: '#e9f4f4',
		aliases: [],
		brands: ['Alenza'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	asuntol: {
		id: 'asuntol',
		name: 'Asuntol',
		hexCode: '#000000',
		aliases: [],
		brands: ['Asuntol'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	attivo_tabs: {
		id: 'attivo_tabs',
		name: 'Attivo® Tabs',
		hexCode: '#952a29',
		aliases: [],
		brands: ['Attivo® Tabs'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	avipro: {
		id: 'avipro',
		name: 'Avipro',
		hexCode: '#da2128',
		aliases: [],
		brands: ['Avipro'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	avipro_grey: {
		id: 'avipro_grey',
		name: 'Avipro Grey',
		hexCode: '#929292',
		aliases: [],
		brands: ['Avipro Grey'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	avipro_red: {
		id: 'avipro_red',
		name: 'Avipro Red',
		hexCode: '#d51317',
		aliases: [],
		brands: ['Avipro Red'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	aviblue_light_blue: {
		id: 'aviblue_light_blue',
		name: 'AviBlue Light Blue',
		hexCode: '#41c0f0',
		aliases: [],
		brands: ['AviBlue Light Blue'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	aviblue_mid_blue: {
		id: 'aviblue_mid_blue',
		name: 'AviBlue Mid Blue',
		hexCode: '#0078BF',
		aliases: [],
		brands: ['AviBlue Mid Blue'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	aviblue_dark_blue: {
		id: 'aviblue_dark_blue',
		name: 'AviBlue Dark Blue',
		hexCode: '#073F65',
		aliases: [],
		brands: ['AviBlue Dark Blue'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	aviblu_campaign_blue: {
		id: 'aviblu_campaign_blue',
		name: 'Aviblu Campaign Blue',
		hexCode: '#00AEEF',
		aliases: [],
		brands: ['Aviblu Campaign Blue'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	avipro_duo_dark_green: {
		id: 'avipro_duo_dark_green',
		name: 'Avipro Duo Dark Green',
		hexCode: '#8DC640',
		aliases: [],
		brands: ['Avipro Duo Dark Green'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	avipro_duo_light_green: {
		id: 'avipro_duo_light_green',
		name: 'Avipro Duo Light Green',
		hexCode: '#E8F4D9',
		textContrast: 'dark',
		aliases: [],
		brands: ['Avipro Duo Light Green'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	avipro_duo_yellow: {
		id: 'avipro_duo_yellow',
		name: 'Avipro Duo Yellow',
		hexCode: '#FDBE35',
		textContrast: 'dark',
		aliases: [],
		brands: ['Avipro Duo Yellow'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	avipro_109_se4_concentrate: {
		id: 'avipro_109_se4_concentrate',
		name: 'Avipro 109 SE4 Concentrate',
		hexCode: '#da2128',
		aliases: [],
		brands: ['Avipro 109 SE4 Concentrate'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	avipro_329_nd_ib2_se4_concentrate: {
		id: 'avipro_329_nd_ib2_se4_concentrate',
		name: 'Avipro 329 ND-IB2-SE4 Concentrate',
		hexCode: '#da2128',
		aliases: [],
		brands: ['Avipro 329 ND-IB2-SE4 Concentrate'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	avipro_megan_egg: {
		id: 'avipro_megan_egg',
		name: 'Avipro Megan Egg',
		hexCode: '#da2128',
		aliases: [],
		brands: ['Avipro Megan Egg'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	avipro_megan_vac_1: {
		id: 'avipro_megan_vac_1',
		name: 'Avipro Megan Vac 1',
		hexCode: '#da2128',
		aliases: [],
		brands: ['Avipro Megan Vac 1'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	avipro_nd_ib_ibd_reo: {
		id: 'avipro_nd_ib_ibd_reo',
		name: 'Avipro ND-IB-IBD-REO',
		hexCode: '#da2128',
		aliases: [],
		brands: ['Avipro ND-IB-IBD-REO'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	avipro_salmonella: {
		id: 'avipro_salmonella',
		name: 'Avipro Salmonella',
		hexCode: '#da2128',
		aliases: [],
		brands: ['Avipro Salmonella'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	avipro_thymovac: {
		id: 'avipro_thymovac',
		name: 'Avipro Thymovac',
		hexCode: '#da2128',
		aliases: [],
		brands: ['Avipro Thymovac'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	bactocell: {
		id: 'bactocell',
		name: 'Bactocell',
		hexCode: '#0db14b',
		aliases: [],
		brands: ['Bactocell'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	bovaer: {
		id: 'bovaer',
		name: 'Bovaer',
		hexCode: '#027d50',
		aliases: [],
		brands: ['Bovaer'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	baycox: {
		id: 'baycox',
		name: 'Baycox',
		hexCode: '#000000',
		aliases: [],
		brands: ['Baycox'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	baycox_5_multi: {
		id: 'baycox_5_multi',
		name: 'Baycox 5% / Multi',
		hexCode: '#000000',
		aliases: [],
		brands: ['Baycox 5% / Multi'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	baycox_iron_injection: {
		id: 'baycox_iron_injection',
		name: 'Baycox Iron Injection',
		hexCode: '#000000',
		aliases: [],
		brands: ['Baycox Iron Injection'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	baycox_iron_oral: {
		id: 'baycox_iron_oral',
		name: 'Baycox Iron Oral',
		hexCode: '#000000',
		aliases: [],
		brands: ['Baycox Iron Oral'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	bayticol_pour_on: {
		id: 'bayticol_pour_on',
		name: 'Bayticol Pour On',
		hexCode: '#000000',
		aliases: [],
		brands: ['Bayticol Pour On'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	baytril_0_5: {
		id: 'baytril_0_5',
		name: 'Baytril 0.5%',
		hexCode: '#c52e5a',
		aliases: [],
		brands: ['Baytril 0.5%'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	baytril_10_injection: {
		id: 'baytril_10_injection',
		name: 'Baytril 10% Injection',
		hexCode: '#c52e5a',
		aliases: [],
		brands: ['Baytril 10% Injection'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	baytril_2_5_oral: {
		id: 'baytril_2_5_oral',
		name: 'Baytril 2.5% Oral',
		hexCode: '#c52e5a',
		aliases: [],
		brands: ['Baytril 2.5% Oral'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	baytril_5_injection: {
		id: 'baytril_5_injection',
		name: 'Baytril 5% Injection',
		hexCode: '#c52e5a',
		aliases: [],
		brands: ['Baytril 5% Injection'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	baytril_100: {
		id: 'baytril_100',
		name: 'Baytril 100',
		hexCode: '#008f87',
		aliases: [],
		brands: ['Baytril 100'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	baytril_flavour_compresse_15mg: {
		id: 'baytril_flavour_compresse_15mg',
		name: 'Baytril Flavour Compresse 15 Mg',
		hexCode: '#9eaf23',
		aliases: [],
		brands: ['Baytril Flavour Compresse 15 Mg'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	baytril_flavour_compresse_150mg: {
		id: 'baytril_flavour_compresse_150mg',
		name: 'Baytril Flavour Compresse 150Mg',
		hexCode: '#a60e1e',
		aliases: [],
		brands: ['Baytril Flavour Compresse 150Mg'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	baytril_flavour_compresse_250mg: {
		id: 'baytril_flavour_compresse_250mg',
		name: 'Baytril Flavour Compresse 250Mg',
		hexCode: '#000000',
		aliases: [],
		brands: ['Baytril Flavour Compresse 250Mg'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	baytril_flavour_compresse_50mg: {
		id: 'baytril_flavour_compresse_50mg',
		name: 'Baytril Flavour Compresse 50Mg',
		hexCode: '#132d7b',
		aliases: [],
		brands: ['Baytril Flavour Compresse 50Mg'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	baytril_flavour_sospensione_orale_per_gatti: {
		id: 'baytril_flavour_sospensione_orale_per_gatti',
		name: 'Baytril Flavour Sospensione Orale Per Gatti',
		hexCode: '#5186c2',
		aliases: [],
		brands: ['Baytril Flavour Sospensione Orale Per Gatti'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	baytril_max: {
		id: 'baytril_max',
		name: 'Baytril Max',
		hexCode: '#000000',
		aliases: [],
		brands: ['Baytril Max'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	baytril_otic: {
		id: 'baytril_otic',
		name: 'Baytril Otic',
		hexCode: '#5a2c72',
		aliases: [],
		brands: ['Baytril Otic'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	baytril_soluzione_iniettabile_25mg_ml: {
		id: 'baytril_soluzione_iniettabile_25mg_ml',
		name: 'Baytril Soluzione Iniettabile 25Mg/Ml',
		hexCode: '#005d9d',
		aliases: [],
		brands: ['Baytril Soluzione Iniettabile 25Mg/Ml'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	bewormready: {
		id: 'bewormready',
		name: 'Bewormready',
		hexCode: '#fc019d',
		aliases: [],
		brands: ['Bewormready'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	bexacat_orange: {
		id: 'bexacat_orange',
		name: 'Bexacat Orange',
		hexCode: '#FF9E18',
		textContrast: 'dark',
		aliases: [],
		brands: ['Bexacat Orange'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
		customStyles: {
			defaultStyle: 'bg-[#FF9E18] text-white',
			collapsed: 'bg-[#cfcbca] text-white hover:bg-[#a19c9c]',
			selected: 'bg-[#FF9E18] text-white hover:bg-[#C25C00]',
		},
	},
	bexacat_dark_grey: {
		id: 'bexacat_dark_grey',
		name: 'Bexacat Dark Grey',
		hexCode: '#262626',
		aliases: [],
		brands: ['Bexacat Dark Grey'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	bexacat_bright_purple: {
		id: 'bexacat_bright_purple',
		name: 'Bexacat Bright Purple',
		hexCode: '#C724B1',
		aliases: [],
		brands: ['Bexacat Bright Purple'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
		customStyles: {
			defaultStyle: 'bg-[#C724B1] text-white',
			collapsed: 'bg-[#cfcbca] text-white hover:bg-[#a19c9c]',
			selected: 'bg-[#C724B1] text-white hover:bg-[#ac1798]',
		},
	},
	bexacat_purple_accent: {
		id: 'bexacat_purple_accent',
		name: 'Bexacat Purple Accent',
		hexCode: '#b24399',
		aliases: [],
		brands: ['Bexacat Purple Accent'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
		customStyles: {
			defaultStyle: 'bg-[#b24399] text-white',
			collapsed: 'bg-[#cfcbca] text-white hover:bg-[#a19c9c]',
			selected: 'bg-[#b24399] text-white hover:bg-[#983c83]',
		},
	},
	bolfo: {
		id: 'bolfo',
		name: 'Bolfo',
		hexCode: '#e3000f',
		lightHexCode: '#fce5e7',
		aliases: [],
		brands: ['Bolfo'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	bovine_ecolizer: {
		id: 'bovine_ecolizer',
		name: 'Bovine Ecolizer',
		hexCode: '#691f74',
		aliases: [],
		brands: ['Bovine Ecolizer'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	bovine_pili_shield: {
		id: 'bovine_pili_shield',
		name: 'Bovine Pili Shield',
		hexCode: '#691f74',
		aliases: [],
		brands: ['Bovine Pili Shield'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	bronch: {
		id: 'bronch',
		name: 'Bronchishield',
		hexCode: '#25076A',
		lightHexCode: '#B6BF00',
		aliases: [],
		brands: ['Bronchishield'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	calcio_ad3_plus_tabs: {
		id: 'calcio_ad3_plus_tabs',
		name: 'Calcio AD3 Plus Tabs',
		hexCode: '#e4007d',
		aliases: [],
		brands: ['Calcio AD3 Plus Tabs'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	capstar: {
		id: 'capstar',
		name: 'Capstar',
		hexCode: '#00633E',
		aliases: [],
		brands: ['Capstar'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	catosal: {
		id: 'catosal',
		name: 'Catosal',
		hexCode: '#000000',
		aliases: [],
		brands: ['Catosal'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	coforta: {
		id: 'coforta',
		name: 'Coforta',
		hexCode: '#000000',
		aliases: [],
		brands: ['Coforta'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	catosal_coforta: {
		id: 'catosal_coforta',
		name: 'Catosal / Coforta',
		hexCode: '#000000',
		aliases: [],
		brands: ['Catosal / Coforta'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	chlorhexl_derm: {
		id: 'chlorhexl_derm',
		name: 'Chlorhexl Derm',
		hexCode: '#b31d2d',
		lightHexCode: '#f7e8ea',
		aliases: [],
		brands: ['Chlorhexl Derm'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	claro_neptra: {
		id: 'claro_neptra',
		name: 'Claro / Neptra',
		hexCode: '#522873',
		aliases: [],
		brands: ['Claro / Neptra'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	clavobay_250mg: {
		id: 'clavobay_250mg',
		name: 'Clavobay 250Mg',
		hexCode: '#7a1971',
		aliases: [],
		brands: ['Clavobay 250Mg'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	clavobay_500mg: {
		id: 'clavobay_500mg',
		name: 'Clavobay 500Mg',
		hexCode: '#ae6a14',
		aliases: [],
		brands: ['Clavobay 500Mg'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	clavobay_50mg: {
		id: 'clavobay_50mg',
		name: 'Clavobay 50Mg',
		hexCode: '#98bc25',
		aliases: [],
		brands: ['Clavobay 50Mg'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	clean_up_2: {
		id: 'clean_up_2',
		name: 'Clean Up II',
		hexCode: '#92C050',
		aliases: [],
		brands: ['Clean Up II'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	clik_light_blue: {
		id: 'clik_light_blue',
		name: 'CLiK Light Blue',
		hexCode: '#0C78BD',
		aliases: [],
		brands: ['CLiK Light Blue'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	clik_dark_blue: {
		id: 'clik_dark_blue',
		name: 'CLiK Dark Blue',
		hexCode: '#1F3560',
		aliases: [],
		brands: ['CLiK Dark Blue'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	clik_yellow: {
		id: 'clik_yellow',
		name: 'CLiK Yellow',
		hexCode: '#DB9300',
		textContrast: 'dark',
		aliases: [],
		brands: ['CLiK Yellow'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	clostratox: {
		id: 'clostratox',
		name: 'Clostratox',
		hexCode: '#006646',
		aliases: [],
		brands: ['Clostratox'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	clostri_shield: {
		id: 'clostri_shield',
		name: 'Clostri Shield',
		hexCode: '#006646',
		aliases: [],
		brands: ['Clostri Shield'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	clostridium_perfringens: {
		id: 'clostridium_perfringens',
		name: 'Clostridium Perfringens',
		hexCode: '#006646',
		aliases: [],
		brands: ['Clostridium Perfringens'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	coban_: {
		id: 'coban_',
		name: 'Coban',
		hexCode: '#ffd200',
		aliases: [],
		brands: ['Coban'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	coliprotec_teal: {
		id: 'coliprotec_teal',
		name: 'Coliprotec Teal',
		hexCode: '#0081a3',
		aliases: [],
		brands: ['Coliprotec Teal'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	correlink: {
		id: 'correlink',
		name: 'Correlink',
		hexCode: '#133156',
		aliases: [],
		brands: ['Correlink'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	comfortis_for_cats: {
		id: 'comfortis_for_cats',
		name: 'Comfortis for Cats',
		hexCode: '#f03127',
		aliases: [],
		brands: ['Comfortis for Cats'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	comfortis_for_dogs: {
		id: 'comfortis_for_dogs',
		name: 'Comfortis for Dogs',
		hexCode: '#f03127',
		aliases: [],
		brands: ['Comfortis for Dogs'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	component_with_tylan: {
		id: 'component_with_tylan',
		name: 'Component With Tylan',
		hexCode: '#004990',
		aliases: [],
		brands: ['Component With Tylan'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	compudose: {
		id: 'compudose',
		name: 'Compudose',
		hexCode: '#ee3124',
		aliases: [],
		brands: ['Compudose'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	cydectin: {
		id: 'cydectin',
		name: 'Cydectin',
		hexCode: '#5C4586',
		aliases: [],
		brands: ['Cydectin'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	onsior_cool_gray_6c: {
		id: 'onsior_cool_gray_6c',
		name: 'Onsior Cool Gray 6 C',
		hexCode: '#a7a8aa',
		aliases: [],
		brands: ['Onsior Cool Gray 6 C'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	onsior_cool_gray_10: {
		id: 'onsior_cool_gray_10',
		name: ' Onsior Cool Gray 10',
		hexCode: '#63666a',
		aliases: [],
		brands: [' Onsior Cool Gray 10'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	cosabody: {
		id: 'cosabody',
		name: 'Cosabody',
		hexCode: '#4c104e',
		aliases: [],
		brands: ['Cosabody'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	covalzin: {
		id: 'covalzin',
		name: 'Covalzin',
		hexCode: '#0072ce',
		aliases: [],
		brands: ['Covalzin'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	credelio_plus: {
		id: 'credelio_plus',
		name: 'Credelio Plus',
		hexCode: '#006341',
		aliases: [],
		brands: ['Credelio Plus'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	credelio_quattro_guard_dog: {
		id: 'credelio_quattro_guard_dog',
		name: 'Credelio Quattro Guard Dog',
		hexCode: '#0F3320',
		aliases: [],
		brands: ['Credelio Quattro Guard Dog'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	credelio_quattro_hunter_green: {
		id: 'credelio_quattro_hunter_green',
		name: 'Credelio Quattro Hunter Green',
		hexCode: '#00643C',
		aliases: [],
		brands: ['Credelio Quattro Hunter Green'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	credelio_quattro_grass_green: {
		id: 'credelio_quattro_grass_green',
		name: 'Credelio Quattro Grass Green',
		hexCode: '#009548',
		aliases: [],
		brands: ['Credelio Quattro Grass Green'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	credelio_quattro_tennis_ball_green: {
		id: 'credelio_quattro_tennis_ball_green',
		name: 'Credelio Quattro Tennis Ball Green',
		hexCode: '#80BB4F',
		aliases: [],
		brands: ['Credelio Quattro Tennis Ball Green'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	credelio_quattro_park_green: {
		id: 'credelio_quattro_park_green',
		name: 'Credelio Quattro Park Green',
		hexCode: '#94CE95',
		textContrast: 'dark',
		aliases: [],
		brands: ['Credelio Quattro Park Green'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	credelio_quattro_golden: {
		id: 'credelio_quattro_golden',
		name: 'Credelio Quattro Golden',
		hexCode: '#F68A33',
		textContrast: 'dark',
		aliases: [],
		brands: ['Credelio Quattro Golden'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	credelio_quattro_milk_bone: {
		id: 'credelio_quattro_milk_bone',
		name: 'Credelio Quattro Milk Bone',
		hexCode: '#EDECDE',
		textContrast: 'dark',
		aliases: [],
		brands: ['Credelio Quattro Milk Bone'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	credelio_quattro_frisbee: {
		id: 'credelio_quattro_frisbee',
		name: 'Credelio Quattro Frisbee',
		hexCode: '#EC008C',
		aliases: [],
		brands: ['Credelio Quattro Frisbee'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	credelio_quattro_sunny_day: {
		id: 'credelio_quattro_sunny_day',
		name: 'Credelio Quattro Sunny Day',
		hexCode: '#EAAC18',
		aliases: [],
		brands: ['Credelio Quattro Sunny Day'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	credelio_quattro_sky_blue: {
		id: 'credelio_quattro_sky_blue',
		name: 'Credelio Quattro Sky Blue',
		hexCode: '#0099D8',
		aliases: [],
		brands: ['Credelio Quattro Sky Blue'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	onsior_dark_blue: {
		id: 'onsior_dark_blue',
		name: 'Onsior Dark Blue',
		hexCode: '#003594',
		aliases: [],
		brands: ['Onsior Dark Blue'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	onsior_dark_gray: {
		id: 'onsior_dark_gray',
		name: 'Onsior Dark Gray',
		hexCode: '#2c2a29',
		aliases: [],
		brands: ['Onsior Dark Gray'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	onsior_dark_red: {
		id: 'onsior_dark_red',
		name: 'Onsior Dark Red',
		hexCode: '#9d2235',
		aliases: [],
		brands: ['Onsior Dark Red'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	onsior_dark_violet: {
		id: 'onsior_dark_violet',
		name: 'Onsior Dark Violet',
		hexCode: '#3c1053',
		aliases: [],
		brands: ['Onsior Dark Violet'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	dasuquin_powder_in: {
		id: 'dasuquin_powder_in',
		name: 'Dasuquin Powder-In',
		hexCode: '#78be20',
		aliases: [],
		brands: ['Dasuquin Powder-In'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	dasuquin_tablet: {
		id: 'dasuquin_tablet',
		name: 'Dasuquin Tablet',
		hexCode: '#78be20',
		aliases: [],
		brands: ['Dasuquin Tablet'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	denagard_12_5_water_soluble_ws: {
		id: 'denagard_12_5_water_soluble_ws',
		name: 'Denagard 12.5% Water Soluble',
		hexCode: '#1d3d7c',
		aliases: [],
		brands: ['Denagard 12.5% Water Soluble'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	denagard_45_granulate: {
		id: 'denagard_45_granulate',
		name: 'Denagard 45% Granulate',
		hexCode: '#1d3d7c',
		aliases: [],
		brands: ['Denagard 45% Granulate'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	denagard_injection: {
		id: 'denagard_injection',
		name: 'Denagard Injection',
		hexCode: '#1d3d7c',
		aliases: [],
		brands: ['Denagard Injection'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	denagard10_premix: {
		id: 'denagard10_premix',
		name: 'Denagard10 Premix',
		hexCode: '#1d3d7c',
		aliases: [],
		brands: ['Denagard10 Premix'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	dog_ear_infections: {
		id: 'dog_ear_infections',
		name: 'Dog Ear Infections',
		hexCode: '#500778',
		lightHexCode: '#ede6f1',
		aliases: [],
		brands: ['Dog Ear Infections'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	droncit_red: {
		id: 'droncit_red',
		name: 'Droncit Red',
		hexCode: '#af2a46',
		aliases: [],
		brands: ['Droncit Red'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	dronspot: {
		id: 'dronspot',
		name: 'Dronspot',
		hexCode: '#49a923',
		lightHexCode: '#ecf6e9',
		aliases: [],
		brands: ['Dronspot'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	drontal: {
		id: 'drontal',
		name: 'Drontal',
		hexCode: '#49a923',
		lightHexCode: '#dceddb',
		aliases: [],
		brands: ['Drontal'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	drontal_plus: {
		id: 'drontal_plus',
		name: 'Drontal Plus',
		hexCode: '#572c83',
		lightHexCode: '#eee9f2',
		aliases: [],
		brands: ['Drontal Plus'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	duramune_lyme: {
		id: 'duramune_lyme',
		name: 'Duramune Lyme',
		hexCode: '#2b529b',
		aliases: [],
		brands: ['Duramune Lyme'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	dvm_daily_soft_chews: {
		id: 'dvm_daily_soft_chews',
		name: 'DVM Daily Soft Chews',
		hexCode: '#0071ce',
		lightHexCode: '#e5f1fa',
		aliases: [],
		brands: ['DVM Daily Soft Chews'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	dvm_feline_joint_gel: {
		id: 'dvm_feline_joint_gel',
		name: 'DVM Feline Joint Gel',
		hexCode: '#d33a2a',
		lightHexCode: '#fbebe9',
		aliases: [],
		brands: ['DVM Feline Joint Gel'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	elancoban: {
		id: 'elancoban',
		name: 'Elancoban',
		hexCode: '#ffd200',
		aliases: [],
		brands: ['Elancoban'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	elanco_grey: {
		id: 'elanco_grey',
		name: 'Elanco Grey',
		hexCode: '#7F7F7F',
		aliases: [],
		brands: ['Elanco Grey'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	endurosyn: {
		id: 'endurosyn',
		name: 'Endurosyn',
		hexCode: '#006937',
		lightHexCode: '#E5F0EB',
		aliases: [],
		brands: ['Endurosyn'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	encore: {
		id: 'encore',
		name: 'Encore',
		hexCode: '#ee3124',
		aliases: [],
		brands: ['Encore'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	ethicon: {
		id: 'ethicon',
		name: 'Ethicon',
		hexCode: '#f30617',
		aliases: [],
		brands: ['Ethicon'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	experior: {
		id: 'experior',
		name: 'Experior',
		hexCode: '#77c043',
		// 'experior_green' is the legacy CMS dropdown codename
		aliases: ['experior_green'],
		brands: ['Experior'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	experior_green: {
		// for legacy support
		id: 'experior',
		name: 'Experior',
		hexCode: '#77c043', // 'experior_green'
		aliases: ['experior_green'],
		brands: ['Experior'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	feedaid: {
		id: 'feedaid',
		name: 'FeedAid',
		hexCode: '#729786',
		aliases: [],
		brands: ['FeedAid'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	fel_o_guard: {
		id: 'fel_o_guard',
		name: 'Fel-O-Guard',
		hexCode: '#004b8d',
		aliases: [],
		brands: ['Fel-O-Guard'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	fel_o_vax: {
		id: 'fel_o_vax',
		name: 'Fel-O-Vax',
		hexCode: '#004b8d',
		aliases: [],
		brands: ['Fel-O-Vax'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	fleegard: {
		id: 'fleegard',
		name: 'Fleegard',
		hexCode: '#da1715',
		aliases: [],
		brands: ['Fleegard'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	flubenvet: {
		id: 'flubenvet',
		name: 'Flubenvet',
		hexCode: '#788e1d',
		aliases: [],
		brands: ['Flubenvet'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	formalife_fermenti_lattice: {
		id: 'formalife_fermenti_lattice',
		name: 'Formalife Fermenti Lattice',
		hexCode: '#005897',
		aliases: [],
		brands: ['Formalife Fermenti Lattice'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	fortekor: {
		id: 'fortekor',
		name: 'Fortekor',
		hexCode: '#f21740',
		aliases: [],
		brands: ['Fortekor'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	fortekor_plus: {
		id: 'fortekor_plus',
		name: 'Fortekor Plus',
		hexCode: '#f21740',
		aliases: [],
		brands: ['Fortekor Plus'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	free_form: {
		id: 'free_form',
		name: 'Free Form',
		hexCode: '#27a9e1',
		lightHexCode: '#e9f6fc',
		aliases: [],
		brands: ['Free Form'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	full_value_pork_primary_blue: {
		id: 'full_value_pork_primary_blue',
		name: 'Full Value Pork Primary Blue',
		hexCode: '#004267',
		aliases: [],
		brands: ['Full Value Pork Primary Blue'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	full_value_pork_secondary_blue: {
		id: 'full_value_pork_secondary_blue',
		name: 'Full Value Pork Secondary Blue',
		hexCode: '#30a9d7',
		aliases: [],
		brands: ['Full Value Pork Secondary Blue'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	fusogard: {
		id: 'fusogard',
		name: 'Fusogard',
		hexCode: '#000b8c',
		aliases: [],
		brands: ['Fusogard'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	galliprant_yellow: {
		id: 'galliprant_yellow',
		name: 'Galliprant Yellow',
		hexCode: '#ffc600',
		textContrast: 'dark',
		aliases: [],
		brands: ['Galliprant Yellow'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	galliprant_red: {
		id: 'galliprant_red',
		name: 'Galliprant Red',
		hexCode: '#d11947',
		aliases: [],
		brands: ['Galliprant Red'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	gallipro_fit_yellow: {
		id: 'gallipro_fit_yellow',
		name: 'Gallipro Fit yellow',
		hexCode: '#FBBA00',
		textContrast: 'dark',
		aliases: [],
		brands: ['Gallipro Fit yellow'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	gallipro_fit_orange: {
		id: 'gallipro_fit_orange',
		name: 'Gallipro Fit orange',
		hexCode: '#EF7900',
		textContrast: 'dark',
		aliases: [],
		brands: ['Gallipro Fit orange'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	gallipro_fit_green: {
		id: 'gallipro_fit_green',
		name: 'Gallipro Fit green',
		hexCode: '#9EB184',
		textContrast: 'dark',
		aliases: [],
		brands: ['Gallipro Fit green'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	gallipro_fit_darkblue: {
		id: 'gallipro_fit_darkblue',
		name: 'Gallipro Fit darkblue',
		hexCode: '#142C46',
		aliases: [],
		brands: ['Gallipro Fit darkblue'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	ganaseg: {
		id: 'ganaseg',
		name: 'Ganaseg',
		hexCode: '#006c68',
		aliases: [],
		brands: ['Ganaseg'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	guardian: {
		id: 'guardian',
		name: 'Guardian',
		hexCode: '#dc0a21',
		aliases: [],
		brands: ['Guardian'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	heifermax: {
		id: 'heifermax',
		name: 'Heifermax',
		hexCode: '#ce0058',
		aliases: [],
		brands: ['Heifermax'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	hemicell_red: {
		id: 'hemicell_red',
		name: 'Hemicell Red',
		hexCode: '#b32b2e',
		aliases: [],
		brands: ['Hemicell Red'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	hemicell_dry: {
		id: 'hemicell_dry',
		name: 'Hemicell Dry',
		hexCode: '#30b19d',
		aliases: [],
		brands: ['Hemicell Dry'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	hemicell_ht: {
		id: 'hemicell_ht',
		name: 'Hemicell HT',
		hexCode: '#a3262c',
		aliases: [],
		brands: ['Hemicell HT'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	hemicell_liquid: {
		id: 'hemicell_liquid',
		name: 'Hemicell Liquid',
		hexCode: '#a3262c',
		aliases: [],
		brands: ['Hemicell Liquid'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	hinocare_dailycare: {
		id: 'hinocare_dailycare',
		name: 'Hinocare Dailycare',
		hexCode: '#8a0317',
		aliases: [],
		brands: ['Hinocare Dailycare'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	hinocare_for_professional_skincare_lotion: {
		id: 'hinocare_for_professional_skincare_lotion',
		name: 'Hinocare For Professional Skincare Lotion',
		hexCode: '#0047bb',
		aliases: [],
		brands: ['Hinocare For Professional Skincare Lotion'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	hinocare_for_professional_skincare_shampoo: {
		id: 'hinocare_for_professional_skincare_shampoo',
		name: 'Hinocare For Professional Skincare Shampoo',
		hexCode: '#0047bb',
		aliases: [],
		brands: ['Hinocare For Professional Skincare Shampoo'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	htsi: {
		id: 'htsi',
		name: 'HTSI',
		hexCode: '#004268',
		lightHexCode: '#e5f0ec',
		aliases: [],
		brands: ['HTSI'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	hylyt: {
		id: 'hylyt',
		name: 'HyLyt',
		hexCode: '#026747',
		lightHexCode: '#e5f0ec',
		aliases: [],
		brands: ['HyLyt'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	increxxa_25: {
		id: 'increxxa_25',
		name: 'Increxxa 25',
		hexCode: '#0057b7',
		aliases: [],
		brands: ['Increxxa 25'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	joki_dent_classic: {
		id: 'joki_dent_classic',
		name: 'Joki Dent Classic',
		hexCode: '#00a49f',
		aliases: [],
		brands: ['Joki Dent Classic'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	joki_dent_fresh: {
		id: 'joki_dent_fresh',
		name: 'Joki Dent Fresh',
		hexCode: '#00a49f',
		aliases: [],
		brands: ['Joki Dent Fresh'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	joki_dent_oral_care_gatto: {
		id: 'joki_dent_oral_care_gatto',
		name: 'Joki Dent Oral Care Gatto',
		hexCode: '#00a49f',
		aliases: [],
		brands: ['Joki Dent Oral Care Gatto'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	joki_dent_vegetal: {
		id: 'joki_dent_vegetal',
		name: 'Joki Dent Vegetal',
		hexCode: '#00a49f',
		aliases: [],
		brands: ['Joki Dent Vegetal'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	kavault: {
		id: 'kavault',
		name: 'Kavault',
		hexCode: '#996633',
		aliases: [],
		brands: ['Kavault'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	ketotest: {
		id: 'ketotest',
		name: 'Ketotest',
		hexCode: '#0072ce',
		aliases: [],
		brands: ['Ketotest'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	kexxtone: {
		id: 'kexxtone',
		name: 'Kexxtone',
		hexCode: '#FFD000',
		textContrast: 'dark',
		aliases: [],
		brands: ['Kexxtone'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	kinetomax: {
		id: 'kinetomax',
		name: 'Kinetomax',
		hexCode: '#105781',
		aliases: [],
		brands: ['Kinetomax'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	kltix: {
		id: 'kltix',
		name: 'Kiltix Pink',
		hexCode: '#cd007d',
		aliases: [],
		brands: ['Kiltix Pink'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	kiltix: {
		id: 'kiltix',
		name: 'Kiltix Teal',
		hexCode: '#0094bc',
		lightHexCode: '#fae7f2',
		aliases: [],
		brands: ['Kiltix Teal'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	lactoqull: {
		id: 'lactoqull',
		name: 'Lactoqull',
		hexCode: '#009560',
		lightHexCode: '#e5f4ef',
		aliases: [],
		brands: ['Lactoqull'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	lanirat: {
		id: 'lanirat',
		name: 'Lanirat',
		hexCode: '#077a77',
		aliases: [],
		brands: ['Lanirat'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	lepto_shield: {
		id: 'lepto_shield',
		name: 'Lepto Shield',
		hexCode: '#000000',
		aliases: [],
		brands: ['Lepto Shield'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	levucell_sb: {
		id: 'levucell_sb',
		name: 'Levucell SB',
		hexCode: '#42aad6',
		aliases: [],
		brands: ['Levucell SB'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	onsior_lime_green: {
		id: 'onsior_lime_green',
		name: 'Onsior Lime Green',
		hexCode: '#00b140',
		aliases: [],
		brands: ['Onsior Lime Green'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	light_gray: {
		id: 'light_gray',
		name: 'Light Gray',
		hexCode: '#f2f2f2',
		textContrast: 'dark',
		aliases: [],
		brands: ['Light Gray'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	loncor: {
		id: 'loncor',
		name: 'Loncor',
		hexCode: '#f1c631',
		aliases: [],
		brands: ['Loncor'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	malaseb: {
		id: 'malaseb',
		name: 'Malaseb',
		hexCode: '#b31d2d',
		lightHexCode: '#F7E8EA',
		aliases: [],
		brands: ['Malaseb'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	masterguard: {
		id: 'masterguard',
		name: 'Masterguard',
		hexCode: '#b9ab97',
		aliases: [],
		brands: ['Masterguard'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	maxiban_: {
		id: 'maxiban_',
		name: 'Maxiban',
		hexCode: '#d31245',
		aliases: [],
		brands: ['Maxiban'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	micotil: {
		id: 'micotil',
		name: 'Micotil',
		hexCode: '#13b5ea',
		aliases: [],
		brands: ['Micotil'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	milbemax: {
		id: 'milbemax',
		name: 'Milbemax',
		hexCode: '#004e8f',
		lightHexCode: '#83AFD6',
		aliases: [],
		brands: ['Milbemax'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	milbemax_blue: {
		id: 'milbemax_blue',
		name: 'Milbemax Blue',
		hexCode: '#004e8f',
		lightHexCode: '#83AFD6',
		aliases: [],
		brands: ['Milbemax Blue'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	milbemax_red: {
		id: 'milbemax_red',
		name: 'Milbemax Red',
		hexCode: '#B10035',
		lightHexCode: '#F9E5EB',
		aliases: [],
		brands: ['Milbemax Red'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	murnil_plus_tabs: {
		id: 'murnil_plus_tabs',
		name: 'Murnil Plus Tabs',
		hexCode: '#00683b',
		aliases: [],
		brands: ['Murnil Plus Tabs'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	myco_shiled: {
		id: 'myco_shiled',
		name: 'Myco Shiled',
		hexCode: '#fec860',
		aliases: [],
		brands: ['Myco Shiled'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	neo_calcio_ad3_polvere: {
		id: 'neo_calcio_ad3_polvere',
		name: 'Neo Calcio AD3 Polvere',
		hexCode: '#e4007d',
		aliases: [],
		brands: ['Neo Calcio AD3 Polvere'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	neomineralsalz_reintegrante: {
		id: 'neomineralsalz_reintegrante',
		name: 'Neomineralsalz Reintegrante',
		hexCode: '#007599',
		aliases: [],
		brands: ['Neomineralsalz Reintegrante'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	neptra_green: {
		id: 'neptra_green',
		name: 'Neptra green',
		hexCode: '#c7d530',
		textContrast: 'dark',
		aliases: [],
		brands: ['Neptra green'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	nuplura_ph: {
		id: 'nuplura_ph',
		name: 'Nuplura PH',
		hexCode: '#a4cf5f',
		aliases: [],
		brands: ['Nuplura PH'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	nuplura_ph_plus_5_primary: {
		id: 'nuplura_ph_plus_5_primary',
		name: 'Nuplura PH+5 Primary',
		hexCode: '#a1d060',
		aliases: [],
		brands: ['Nuplura PH+5 Primary'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	nuplura_ph_plus_5_secondary: {
		id: 'nuplura_ph_plus_5_secondary',
		name: 'Nuplura PH+5 Secondary',
		hexCode: '#8b8c90',
		aliases: [],
		brands: ['Nuplura PH+5 Secondary'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	optaflexx: {
		id: 'optaflexx',
		name: 'Optaflexx',
		hexCode: '#00a18e',
		aliases: [],
		brands: ['Optaflexx'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	otlrinse: {
		id: 'otlrinse',
		name: 'OtlRinse',
		hexCode: '#1b81bb',
		lightHexCode: '#e8f2f8',
		aliases: [],
		brands: ['OtlRinse'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	otodog: {
		id: 'otodog',
		name: 'Otodog',
		hexCode: '#007bbf',
		aliases: [],
		brands: ['Otodog'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	panalog: {
		id: 'panalog',
		name: 'Panalog',
		hexCode: '#003ca6',
		aliases: [],
		brands: ['Panalog'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	para_shield: {
		id: 'para_shield',
		name: 'Para Shield',
		hexCode: '#b6363a',
		aliases: [],
		brands: ['Para Shield'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	parvo: {
		id: 'parvo',
		name: 'Parvo',
		hexCode: '#2B71B8',
		aliases: [],
		brands: ['Parvo'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	parvo_shiled_l5e: {
		id: 'parvo_shiled_l5e',
		name: 'Parvo Shiled L5E',
		hexCode: '#7f2629',
		aliases: [],
		brands: ['Parvo Shiled L5E'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	pet_protect_red: {
		id: 'pet_protect_red',
		name: 'Pet Protect - Red',
		hexCode: '#F9423A',
		aliases: [],
		brands: ['Pet Protect - Red'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	pet_protect_green: {
		id: 'pet_protect_green',
		name: 'Pet Protect - Green',
		hexCode: '#13863D',
		aliases: [],
		brands: ['Pet Protect - Green'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	pet_protect_blue: {
		id: 'pet_protect_blue',
		name: 'Pet Protect - Blue',
		hexCode: '#49B2E6',
		aliases: [],
		brands: ['Pet Protect - Blue'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	pet_protect_skin: {
		id: 'pet_protect_skin',
		name: 'Pet Protect Skin',
		hexCode: '#25B0E7',
		aliases: [],
		brands: ['Pet Protect Skin'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	pet_protect_calm: {
		id: 'pet_protect_calm',
		name: 'Pet Protect Calm',
		hexCode: '#C05694',
		aliases: [],
		brands: ['Pet Protect Calm'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	pet_protect_allergy: {
		id: 'pet_protect_allergy',
		name: 'Pet Protect Allergy',
		hexCode: '#70B925',
		aliases: [],
		brands: ['Pet Protect Allergy'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	civ: {
		id: 'civ',
		name: 'CIV',
		hexCode: '#CFB62E',
		aliases: [],
		brands: ['CIV'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	elmaro_navy_blue: {
		id: 'elmaro_navy_blue',
		name: 'Elmaro Navy Blue',
		hexCode: '#00457f',
		aliases: [],
		brands: ['Elmaro Navy Blue'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	elmaro_pink: {
		id: 'elmaro_pink',
		name: 'Elmaro Pink',
		hexCode: '#eb7bae',
		aliases: [],
		brands: ['Elmaro Pink'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	elmaro_yellow: {
		id: 'elmaro_yellow',
		name: 'Elmaro Yellow',
		hexCode: '#fbba00',
		aliases: [],
		brands: ['Elmaro Yellow'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	pili_shield_plus_c: {
		id: 'pili_shield_plus_c',
		name: 'Pili Shield +C',
		hexCode: '#7f2629',
		aliases: [],
		brands: ['Pili Shield +C'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	pillpocket: {
		id: 'pillpocket',
		name: 'Pillpocket',
		hexCode: '#046a38',
		aliases: [],
		brands: ['Pillpocket'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	pinkeye_shield_xt4: {
		id: 'pinkeye_shield_xt4',
		name: 'Pinkeye Shield XT4',
		hexCode: '#df1683',
		aliases: [],
		brands: ['Pinkeye Shield XT4'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	prefender: {
		id: 'prefender',
		name: 'Profender',
		hexCode: '#FEC057',
		lightHexCode: '#fff9ee',
		aliases: [],
		brands: ['Profender'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	prevacent_prrs: {
		id: 'prevacent_prrs',
		name: 'Prevacent PRRS',
		hexCode: '#d80516',
		aliases: [],
		brands: ['Prevacent PRRS'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	primapappa_cucciolo: {
		id: 'primapappa_cucciolo',
		name: 'Primapappa Cucciolo',
		hexCode: '#e75c73',
		aliases: [],
		brands: ['Primapappa Cucciolo'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	primapappa_gattino: {
		id: 'primapappa_gattino',
		name: 'Primapappa Gattino',
		hexCode: '#e75c73',
		aliases: [],
		brands: ['Primapappa Gattino'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	primolatte_cucciolo: {
		id: 'primolatte_cucciolo',
		name: 'Primolatte Cucciolo',
		hexCode: '#00a2de',
		aliases: [],
		brands: ['Primolatte Cucciolo'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	primolatte_gattino: {
		id: 'primolatte_gattino',
		name: 'Primolatte Gattino',
		hexCode: '#00a2de',
		aliases: [],
		brands: ['Primolatte Gattino'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	pulmotil: {
		id: 'pulmotil',
		name: 'Pulmotil',
		hexCode: '#b92f92',
		aliases: [],
		brands: ['Pulmotil'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	pulmotil_100_pre_mix: {
		id: 'pulmotil_100_pre_mix',
		name: 'Pulmotil 100 Pre-Mix',
		hexCode: '#0093d1',
		aliases: [],
		brands: ['Pulmotil 100 Pre-Mix'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	pulmotil_40_pre_mix: {
		id: 'pulmotil_40_pre_mix',
		name: 'Pulmotil 40 Pre-Mix',
		hexCode: '#0093d1',
		aliases: [],
		brands: ['Pulmotil 40 Pre-Mix'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	pulmotil_ac: {
		id: 'pulmotil_ac',
		name: 'Pulmotil AC',
		hexCode: '#bc2584',
		aliases: [],
		brands: ['Pulmotil AC'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	pulmotil_premix: {
		id: 'pulmotil_premix',
		name: 'Pulmotil Premix',
		hexCode: '#b92f92',
		aliases: [],
		brands: ['Pulmotil Premix'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	quad_dewormer: {
		id: 'quad_dewormer',
		name: 'Quad Dewormer',
		hexCode: '#9c921e',
		lightHexCode: '#f5f4e8',
		aliases: [],
		brands: ['Quad Dewormer'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	quick_bayt: {
		id: 'quick_bayt',
		name: 'Quick Bayt',
		hexCode: '#000000',
		aliases: [],
		brands: ['Quick Bayt'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	quick_shield: {
		id: 'quick_shield',
		name: 'Quick Shield',
		hexCode: '#5c6670',
		aliases: [],
		brands: ['Quick Shield'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	quellin: {
		id: 'quellin',
		name: 'Quellin',
		hexCode: '#002b54',
		aliases: [],
		brands: ['Quellin'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	rellef: {
		id: 'rellef',
		name: 'Rellef',
		hexCode: '#0c8495',
		lightHexCode: '#e6f2f4',
		aliases: [],
		brands: ['Rellef'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	remend: {
		id: 'remend',
		name: 'Remend',
		hexCode: '#462777',
		lightHexCode: '#ece9f1',
		aliases: [],
		brands: ['Remend'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	reprostar: {
		id: 'reprostar',
		name: 'Reprostar',
		hexCode: '#000000',
		aliases: [],
		brands: ['Reprostar'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	rhini_shield: {
		id: 'rhini_shield',
		name: 'Rhini Shield',
		hexCode: '#009383',
		aliases: [],
		brands: ['Rhini Shield'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	onsior_rhodamine_red: {
		id: 'onsior_rhodamine_red',
		name: 'Onsior Rhodamine Red',
		hexCode: '#e10098',
		aliases: [],
		brands: ['Onsior Rhodamine Red'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	rimuovipelo_gatto: {
		id: 'rimuovipelo_gatto',
		name: 'Rimuovipelo Gatto',
		hexCode: '#00694f',
		aliases: [],
		brands: ['Rimuovipelo Gatto'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	salmonella_360: {
		id: 'salmonella_360',
		name: 'Salmonella 360',
		hexCode: '#EE7D00',
		textContrast: 'dark',
		aliases: [],
		brands: ['Salmonella 360'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	zenrelia_primary: {
		id: 'zenrelia_primary',
		name: 'Zenrelia Primary',
		hexCode: '#215447',
		aliases: [],
		brands: ['Zenrelia Primary'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	zenrelia_secondary: {
		id: 'zenrelia_secondary',
		name: 'Zenrelia Secondary',
		hexCode: '#91CC12',
		textContrast: 'dark',
		aliases: [],
		brands: ['Zenrelia Secondary'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	zenrelia_contrast: {
		id: 'zenrelia_contrast',
		name: 'Zenrelia Contrast',
		hexCode: '#A68CFA',
		textContrast: 'dark',
		aliases: [],
		brands: ['Zenrelia Contrast'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	zenrelia_accent: {
		id: 'zenrelia_accent',
		name: 'Zenrelia Accent',
		hexCode: '#c0d29a',
		textContrast: 'dark',
		aliases: [],
		brands: ['Zenrelia Accent'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	zenrelia_sku1: {
		id: 'zenrelia_sku1',
		name: 'Zenrelia Sku1',
		hexCode: '#618582',
		textContrast: 'dark',
		aliases: [],
		brands: ['Zenrelia Sku1'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	zenrelia_sku2: {
		id: 'zenrelia_sku2',
		name: 'Zenrelia Sku2',
		hexCode: '#8752C7',
		aliases: [],
		brands: ['Zenrelia Sku2'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	zenrelia_sku3: {
		id: 'zenrelia_sku3',
		name: 'Zenrelia Sku3',
		hexCode: '#00A99E',
		textContrast: 'dark',
		aliases: [],
		brands: ['Zenrelia Sku3'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	zenrelia_sku4: {
		id: 'zenrelia_sku4',
		name: 'Zenrelia Sku4',
		hexCode: '#C6B5FF',
		textContrast: 'dark',
		aliases: [],
		brands: ['Zenrelia Sku4'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	scour_bos: {
		id: 'scour_bos',
		name: 'Scour Bos',
		hexCode: '#6a2875',
		aliases: [],
		brands: ['Scour Bos'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	skycis: {
		id: 'skycis',
		name: 'Skycis',
		hexCode: '#fdc27f',
		aliases: [],
		brands: ['Skycis'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	somnu_shield: {
		id: 'somnu_shield',
		name: 'Somnu Shield',
		hexCode: '#000000',
		aliases: [],
		brands: ['Somnu Shield'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	standguard: {
		id: 'standguard',
		name: 'Standguard',
		hexCode: '#ee3124',
		aliases: [],
		brands: ['Standguard'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	surmax_100: {
		id: 'surmax_100',
		name: 'Surmax 100',
		hexCode: '#996633',
		aliases: [],
		brands: ['Surmax 100'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	surmax_200: {
		id: 'surmax_200',
		name: 'Surmax 200',
		hexCode: '#996633',
		aliases: [],
		brands: ['Surmax 200'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	surmax_maxus_inteprity: {
		id: 'surmax_maxus_inteprity',
		name: 'Surmax / Maxus / Inteprity',
		hexCode: '#996633',
		aliases: [],
		brands: ['Surmax / Maxus / Inteprity'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	synovl_g3: {
		id: 'synovl_g3',
		name: 'Synovl G3',
		hexCode: '#ee483c',
		lightHexCode: '#fdeceb',
		aliases: [],
		brands: ['Synovl G3'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	synovl_g4: {
		id: 'synovl_g4',
		name: 'Synovl G4',
		hexCode: '#f5854f',
		lightHexCode: '#fef3ed',
		aliases: [],
		brands: ['Synovl G4'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	t8_keto: {
		id: 't8_keto',
		name: 'T8 Keto',
		hexCode: '#563393',
		lightHexCode: '#eeeaf4',
		aliases: [],
		brands: ['T8 Keto'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	onsior_tangerine: {
		id: 'onsior_tangerine',
		name: 'Onsior Tangerine',
		hexCode: '#ffb81c',
		aliases: [],
		brands: ['Onsior Tangerine'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	tapeworm_dewormer_for_cats: {
		id: 'tapeworm_dewormer_for_cats',
		name: 'Tapeworm Dewormer for Cats',
		hexCode: '#f27f2d',
		lightHexCode: '#fef2ea',
		aliases: [],
		brands: ['Tapeworm Dewormer for Cats'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	tapeworm_dewormer_for_dogs: {
		id: 'tapeworm_dewormer_for_dogs',
		name: 'Tapeworm Dewormer for Dogs',
		hexCode: '#735121',
		lightHexCode: '#f1ede8',
		aliases: [],
		brands: ['Tapeworm Dewormer for Dogs'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	tempo_one_percent_dust: {
		id: 'tempo_one_percent_dust',
		name: 'Tempo 1% Dust',
		hexCode: '#75bc43',
		aliases: [],
		brands: ['Tempo 1% Dust'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	tiguvon: {
		id: 'tiguvon',
		name: 'Tiguvon',
		hexCode: '#0db14b',
		aliases: [],
		brands: ['Tiguvon'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	tracer: {
		id: 'tracer',
		name: 'Tracer',
		hexCode: '#ed037c',
		aliases: [],
		brands: ['Tracer'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	trucan_blue: {
		id: 'trucan_blue',
		name: 'Trucan Blue',
		hexCode: '#74d7eb',
		aliases: [],
		brands: ['Trucan Blue'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	trufel_pink: {
		id: 'trufel_pink',
		name: 'Trufel Pink',
		hexCode: '#e3c8de',
		aliases: [],
		brands: ['Trufel Pink'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	trucid: {
		id: 'trucid',
		name: 'Trucid',
		hexCode: '#d72027',
		aliases: [],
		brands: ['Trucid'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	titanium: {
		id: 'titanium',
		name: 'Titanium',
		hexCode: '#6a737b',
		aliases: [],
		brands: ['Titanium'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	tylan_200_injection: {
		id: 'tylan_200_injection',
		name: 'Tylan 200 Injection',
		hexCode: '#ee3124',
		aliases: [],
		brands: ['Tylan 200 Injection'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	tylan_injection: {
		id: 'tylan_injection',
		name: 'Tylan Injection',
		hexCode: '#ee3124',
		aliases: [],
		brands: ['Tylan Injection'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	tylan_premix: {
		id: 'tylan_premix',
		name: 'Tylan Premix',
		hexCode: '#0093d0',
		aliases: [],
		brands: ['Tylan Premix'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	tylan_soluble: {
		id: 'tylan_soluble',
		name: 'Tylan Soluble',
		hexCode: '#0093d0',
		aliases: [],
		brands: ['Tylan Soluble'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	ultra_duramune: {
		id: 'ultra_duramune',
		name: 'ULTRA Duramune',
		hexCode: '#499abc',
		aliases: [],
		brands: ['ULTRA Duramune'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	ultra_fel_o_vax: {
		id: 'ultra_fel_o_vax',
		name: 'Ultra Fel-O-Vax',
		hexCode: '#f5a3c1',
		aliases: [],
		brands: ['Ultra Fel-O-Vax'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	ultra_hybrid: {
		id: 'ultra_hybrid',
		name: 'Ultra Hybrid',
		hexCode: '#7276b8',
		aliases: [],
		brands: ['Ultra Hybrid'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	varenzin_red: {
		id: 'varenzin_red',
		name: 'Varenzin Red',
		hexCode: '#bc2251',
		aliases: [],
		brands: ['Varenzin Red'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	varenzin_blue: {
		id: 'varenzin_blue',
		name: 'Varenzin Blue',
		hexCode: '#0056b8',
		aliases: [],
		brands: ['Varenzin Blue'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	varenzin_purple: {
		id: 'varenzin_purple',
		name: 'Varenzin Purple',
		hexCode: '#6a488e',
		aliases: [],
		brands: ['Varenzin Purple'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	veraflox_oral_suspension: {
		id: 'veraflox_oral_suspension',
		name: 'Veraflox Oral Suspension',
		hexCode: '#272268',
		aliases: [],
		brands: ['Veraflox Oral Suspension'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	vetriderm: {
		id: 'vetriderm',
		name: 'Vetriderm',
		hexCode: '#62b51f',
		aliases: [],
		brands: ['Vetriderm'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	vib_shield: {
		id: 'vib_shield',
		name: 'Vib Shield',
		hexCode: '#000000',
		aliases: [],
		brands: ['Vib Shield'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	vira_shield: {
		id: 'vira_shield',
		name: 'Vira Shield',
		hexCode: '#e05a28',
		aliases: [],
		brands: ['Vira Shield'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	onsior_vivid_orange: {
		id: 'onsior_vivid_orange',
		name: 'Onsior Vivid Orange',
		hexCode: '#ff671f',
		aliases: [],
		brands: ['Onsior Vivid Orange'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	zimprova: {
		id: 'zimprova',
		name: 'Zimprova',
		hexCode: '#7a1600',
		aliases: [],
		brands: ['Zimprova'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	zolvix: {
		id: 'zolvix',
		name: 'Zolvix',
		hexCode: '#005043',
		aliases: [],
		brands: ['Zolvix'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	zolvix_orange: {
		id: 'zolvix_orange',
		name: 'Zolvix Orange',
		hexCode: '#FF5F1F',
		textContrast: 'dark',
		aliases: [],
		brands: ['Zolvix Orange'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	rosa_xo_verme: {
		id: 'rosa_xo_verme',
		name: 'Rosa - Xô Verme',
		hexCode: '#e82e8a',
		aliases: [],
		brands: ['Rosa - Xô Verme'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	catosal_metabol: {
		id: 'catosal_metabol',
		name: 'Catosal Metabol',
		hexCode: '#FF3399',
		aliases: [],
		brands: ['Catosal Metabol'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	befrena_primary_blue: {
		id: 'befrena_primary_blue',
		name: 'Befrena Primary Blue',
		hexCode: '#0F4597',
		aliases: [],
		brands: ['Befrena Primary Blue'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	befrena_accent_green: {
		id: 'befrena_accent_green',
		name: 'Befrena Accent Green',
		hexCode: '#8CC63E',
		aliases: [],
		brands: ['Befrena Accent Green'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	befrena_accent_pink: {
		id: 'befrena_accent_pink',
		name: 'Befrena Accent Pink',
		hexCode: '#E79FC6',
		aliases: [],
		brands: ['Befrena Accent Pink'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	befrena_sku4: {
		id: 'befrena_sku4',
		name: 'Befrena SKU 4',
		hexCode: '#95157b',
		aliases: [],
		brands: ['Befrena SKU 4'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	zolvix_plus_green: {
		id: 'zolvix_plus_green',
		name: 'Zolvix Plus Green',
		hexCode: '#105742',
		aliases: [],
		brands: ['Zolvix Plus Green'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
		customStyles: {
			defaultStyle: 'bg-[#105742] text-white hover:bg-[#003c2f]',
			collapsed: 'bg-[#3b8e7b] text-white',
			selected: 'bg-[#105742] text-white',
		},
	},
	zolvix_plus_orange: {
		id: 'zolvix_plus_orange',
		name: 'Zolvix Plus Orange',
		hexCode: '#ED7203',
		aliases: [],
		brands: ['Zolvix Plus Orange'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
		customStyles: {
			defaultStyle: 'bg-[#ED7203] text-white hover:bg-[#C25C00]',
			collapsed: 'bg-[#F59E4C] text-white',
			selected: 'bg-[#ED7203] text-white',
		},
	},
	eluracat_dark_pink: {
		id: 'eluracat_dark_pink',
		name: 'Eluracat Dark Pink',
		hexCode: '#AB1368',
		aliases: [],
		brands: ['Eluracat Dark Pink'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	eluracat_light_pink: {
		id: 'eluracat_light_pink',
		name: 'Eluracat Light Pink',
		hexCode: '#E10098',
		aliases: [],
		brands: ['Eluracat Light Pink'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	eluracat_yellow: {
		id: 'eluracat_yellow',
		name: 'Eluracat Yellow',
		hexCode: '#FDBA12',
		aliases: [],
		brands: ['Eluracat Yellow'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	trucan_ultra_blue: {
		id: 'trucan_ultra_blue',
		name: 'Trucan Ultra Blue',
		hexCode: '#1A4278',
		aliases: [],
		brands: ['Trucan Ultra Blue'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	trucan_ultra_yellow: {
		id: 'trucan_ultra_yellow',
		name: 'Trucan Ultra Yellow',
		hexCode: '#FCC924',
		aliases: [],
		brands: ['Trucan Ultra Yellow'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	tru_crew_blue: {
		id: 'tru_crew_blue',
		name: 'Tru Crew Blue',
		hexCode: '#304AC4',
		aliases: [],
		brands: ['Tru Crew Blue'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	tru_crew_mint: {
		id: 'tru_crew_mint',
		name: 'Tru Crew Mint',
		hexCode: '#55E09F',
		aliases: [],
		brands: ['Tru Crew Mint'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	advocate_uc_gold: {
		id: 'advocate_uc_gold',
		name: 'Advocate UC Gold',
		hexCode: '#CCA144',
		aliases: [],
		brands: ['Advocate UC Gold'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	advocate_uc_mid_gold: {
		id: 'advocate_uc_mid_gold',
		name: 'Advocate UC Mid Gold',
		hexCode: '#e5d0a1',
		aliases: [],
		textContrast: 'dark',
		brands: ['Advocate UC Mid Gold'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	inverted_advocate_uc_gold: {
		id: 'inverted_advocate_uc_gold',
		name: 'Advocate UC Gold Inverted',
		hexCode: '#CCA144',
		aliases: [],
		brands: ['Advocate UC Gold'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	inverted_advocate_uc_mid_gold: {
		id: 'inverted_advocate_uc_mid_gold',
		name: 'Advocate UC Mid Gold Inverted',
		hexCode: '#e5d0a1',
		aliases: [],
		textContrast: 'dark',
		brands: ['Advocate UC Mid Gold'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	hemicell_orange: {
		id: 'hemicell_orange',
		name: 'Hemicell Orange',
		hexCode: '#CD6107',
		aliases: [],
		brands: ['Hemicell Orange'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	hemicell_light_orange: {
		id: 'hemicell_light_orange',
		name: 'Hemicell Light Orange',
		hexCode: '#F7A600',
		textContrast: 'dark',
		aliases: [],
		brands: ['Hemicell Light Orange'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	trutect_dark_blue: {
		id: 'trutect_dark_blue',
		name: 'Trutect Dark Blue',
		hexCode: '#001149',
		aliases: [],
		brands: ['Trutect Dark Blue'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	trutect_light_pink: {
		id: 'trutect_light_pink',
		name: 'Trutect Light Pink',
		hexCode: '#f0d6cf',
		textContrast: 'dark',
		aliases: [],
		brands: ['Trutect Light Pink'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	trutect_light_blue: {
		id: 'trutect_light_blue',
		name: 'Trutect Light Blue',
		hexCode: '#789cdb',
		textContrast: 'dark',
		aliases: [],
		brands: ['Trutect Light Blue'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	trutect_yellow: {
		id: 'trutect_yellow',
		name: 'Trutect Yellow',
		hexCode: '#fff729',
		textContrast: 'dark',
		aliases: [],
		brands: ['Trutect Yellow'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	trutect_turquoise: {
		id: 'trutect_turquoise',
		name: 'Trutect Turquoise',
		hexCode: '#88d1d6',
		textContrast: 'dark',
		aliases: [],
		brands: ['Trutect Turquoise'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
		customStyles: {
			defaultStyle: 'bg-[#88d1d6] text-black hover:bg-[#5fbdc4]',
			collapsed: 'bg-[#a8dde0] text-black',
			selected: 'bg-[#88d1d6] text-black',
		},
	},
	// ========================================================================
	// TAILWIND UTILITY COLORS (from legacy tailwind-colors.ts)
	// ========================================================================
	'elanco-beige': {
		id: 'elanco-beige',
		name: 'Elanco Beige',
		hexCode: '#D5C4AF',
		textContrast: 'dark',
		aliases: ['elanco_beige'],
		brands: ['Elanco'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	'elanco-blue': {
		id: 'elanco-blue',
		name: 'Elanco Blue',
		hexCode: '#0072CE',
		aliases: ['elanco_blue'],
		brands: ['Elanco'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	'light-blue': {
		id: 'light-blue',
		name: 'Light Blue',
		hexCode: '#D9EAF8',
		textContrast: 'dark',
		aliases: ['light_blue'],
		brands: [],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	'mid-beige': {
		id: 'mid-beige',
		name: 'Mid Beige',
		hexCode: '#EEE8DF',
		textContrast: 'dark',
		aliases: ['mid_beige'],
		brands: [],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	'midnight-blue': {
		id: 'midnight-blue',
		name: 'Midnight Blue',
		hexCode: '#001149',
		aliases: ['midnight_blue'],
		brands: [],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	pet: {
		id: 'pet',
		name: 'Pet',
		hexCode: '#2B9E3B',
		aliases: [],
		brands: ['Pet'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	vet: {
		id: 'vet',
		name: 'Vet',
		hexCode: '#3818A2',
		aliases: [],
		brands: ['Vet'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	'default-light-background': {
		id: 'default-light-background',
		name: 'Default Light Background',
		hexCode: '#d5c4af',
		textContrast: 'dark',
		aliases: ['default_light_background'],
		brands: [],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	'elanco-green': {
		id: 'elanco-green',
		name: 'Elanco Green',
		hexCode: '#0EC3A4',
		textContrast: 'dark',
		aliases: ['elanco_green'],
		brands: ['Elanco'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	'pet-green-light': {
		id: 'pet-green-light',
		name: 'Pet Green Light',
		hexCode: '#CAE3C8',
		textContrast: 'dark',
		aliases: ['pet_green_light'],
		brands: ['Pet'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	'pet-green': {
		id: 'pet-green',
		name: 'Pet Green',
		hexCode: '#1F8815',
		aliases: ['pet_green', 'pet_primary_green'],
		brands: ['Pet'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	'purple-light': {
		id: 'purple-light',
		name: 'Purple Light',
		hexCode: '#EAE6F5',
		textContrast: 'dark',
		aliases: ['purple_light'],
		brands: [],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	'vet-purple': {
		id: 'vet-purple',
		name: 'Vet Purple',
		hexCode: '#3818A2',
		aliases: ['vet_purple'],
		brands: ['Vet'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	'elanco-dark-navy': {
		id: 'elanco-dark-navy',
		name: 'Elanco Dark Navy',
		hexCode: '#02253e',
		aliases: [],
		brands: ['Elanco'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	'elanco-teal': {
		id: 'elanco-teal',
		name: 'Elanco Teal',
		hexCode: '#00ab9c',
		aliases: [],
		brands: ['Elanco'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	'elanco-mid-blue': {
		id: 'elanco-mid-blue',
		name: 'Elanco Mid Blue',
		hexCode: '#2772ce',
		aliases: [],
		brands: ['Elanco'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	'locator-blue': {
		id: 'locator-blue',
		name: 'Locator Blue',
		hexCode: '#00bfff',
		aliases: [],
		brands: [],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	'panel-grey': {
		id: 'panel-grey',
		name: 'Panel Grey',
		hexCode: '#f8f8f8',
		textContrast: 'dark',
		aliases: [],
		brands: [],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	'dark-charcoal': {
		id: 'dark-charcoal',
		name: 'Dark Charcoal',
		hexCode: '#373737',
		aliases: [],
		brands: [],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	'error-red': {
		id: 'error-red',
		name: 'Error Red',
		hexCode: '#ce4635',
		aliases: [],
		brands: [],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	// ========================================================================
	// CATEGORY COLORS
	// ========================================================================
	behaviour: {
		id: 'behaviour',
		name: 'Behaviour',
		hexCode: '#d43900',
		aliases: [],
		brands: [],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	new_pets: {
		id: 'new_pets',
		name: 'New Pets',
		hexCode: '#bd2ca2',
		aliases: [],
		brands: [],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	lifestyle: {
		id: 'lifestyle',
		name: 'Lifestyle',
		hexCode: '#b50000',
		aliases: [],
		brands: [],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	health_and_care: {
		id: 'health_and_care',
		name: 'Health and Care',
		hexCode: '#138187',
		aliases: [],
		brands: [],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	parasites: {
		id: 'parasites',
		name: 'Parasites',
		hexCode: '#3a76bb',
		aliases: [],
		brands: [],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
	'elanco-grey': {
		id: 'elanco-grey',
		name: 'Elanco Grey',
		hexCode: '#7F7F7F',
		aliases: [],
		brands: ['Elanco Grey'],
		towers: {pet: true, vet: true, farm: true, corporate: true},
	},
}

/**
 * Get a color by ID
 */
export function getColor(colorId: string): ColorInfo | undefined {
	return colorRegistry[colorId]
}

/**
 * Get a color by alias (backward compatibility)
 * Case-insensitive matching for flexibility
 */
export function getColorByAlias(alias: string): ColorInfo | undefined {
	const normalizedAlias = alias.toLowerCase()
	return Object.values(colorRegistry).find((color) =>
		color.aliases.some((a) => a.toLowerCase() === normalizedAlias)
	)
}

/**
 * Get all colors for a specific tower
 */
export function getColorsByTower(
	tower: 'pet' | 'vet' | 'farm' | 'corporate'
): ColorInfo[] {
	return Object.values(colorRegistry).filter((color) => color.towers[tower])
}

/**
 * Get all colors
 */
export function getAllColors(): ColorInfo[] {
	return Object.values(colorRegistry)
}

/**
 * Get color by brand name
 * @example getColorByBrand('Seresto') // Returns green color
 */
export function getColorByBrand(brandName: string): ColorInfo | undefined {
	const normalizedBrand = brandName.toLowerCase()
	return Object.values(colorRegistry).find((color) =>
		color.brands.some((b) => b.toLowerCase() === normalizedBrand)
	)
}

/**
 * Get all colors for a specific brand
 * @example getColorsByBrand('Seresto') // Returns all colors used by Seresto
 */
export function getColorsByBrand(brandName: string): ColorInfo[] {
	const normalizedBrand = brandName.toLowerCase()
	return Object.values(colorRegistry).filter((color) =>
		color.brands.some((b) => b.toLowerCase() === normalizedBrand)
	)
}

/**
 * Get all unique brand names from the registry
 */
export function getAllBrands(): string[] {
	const brands = new Set<string>()
	Object.values(colorRegistry).forEach((color) => {
		color.brands.forEach((brand) => brands.add(brand))
	})
	return Array.from(brands).sort((a, b) => a.localeCompare(b))
}

/**
 * Check if a brand exists in the registry
 */
export function isBrandRegistered(brandName: string): boolean {
	const normalizedBrand = brandName.toLowerCase()
	return Object.values(colorRegistry).some((color) =>
		color.brands.some((b) => b.toLowerCase() === normalizedBrand)
	)
}

// ============================================================================
// TAILWIND CSS HELPERS
// ============================================================================

interface ColorClasses {
	text: string
	bg: string
	border: string
	hover: string
	/** Light background variant */
	bgLight: string
	/** Ring/focus color */
	ring: string
}

/**
 * Get Tailwind CSS classes for a color ID or alias
 * Compatible with legacy getBrandColorClasses() function
 * @example getColorClasses('seresto') returns text, bg, border, hover classes
 */
export function getColorClasses(
	colorId: string,
	fallback = 'elanco-blue'
): ColorClasses {
	const color = getColor(colorId) || getColorByAlias(colorId)
	const id = color?.id || fallback
	return {
		text: `text-${id}`,
		bg: `bg-${id}`,
		border: `border-${id}`,
		hover: `hover:bg-${id}/10`,
		bgLight: `bg-${id}/10`,
		ring: `ring-${id}`,
	}
}

/**
 * Get the hex code for a color ID or alias
 * @example getHexCode('seresto') returns '#00597c'
 */
export function getHexCode(colorId: string): string | undefined {
	const color = getColor(colorId) || getColorByAlias(colorId)
	return color?.hexCode
}

/**
 * Get the hex code for a color ID or alias, throwing if not found.
 * Use this when the color is guaranteed to exist in the registry.
 * @throws Error if the color is not found in the registry
 * @example requireHexCode('elanco-dark-navy') returns '#02253e'
 */
export function requireHexCode(colorId: string): string {
	const hex = getHexCode(colorId)
	if (hex === undefined) {
		throw new Error(
			`Color "${colorId}" not found in registry. Ensure it exists before using requireHexCode.`
		)
	}
	return hex
}

// ============================================================================
// KONTENT CMS HELPERS
// ============================================================================

interface KontentColorOption {
	codename: string
	name: string
}

/**
 * Get all colors formatted for Kontent CMS multiple choice dropdown
 * This is the single source of truth for CMS color options
 * @example Used in model-as-code: options: getColorsForCMS()
 */
export function getColorsForCMS(): KontentColorOption[] {
	return Object.values(colorRegistry).map((color) => ({
		codename: color.id,
		name: color.name,
	}))
}

/**
 * Get colors for a specific tower formatted for Kontent CMS
 * @example getColorsForCMSByTower('pet') // Only pet-enabled colors
 */
export function getColorsForCMSByTower(
	tower: 'pet' | 'vet' | 'farm' | 'corporate'
): KontentColorOption[] {
	return Object.values(colorRegistry)
		.filter((color) => color.towers[tower])
		.map((color) => ({
			codename: color.id,
			name: color.name,
		}))
}

// ============================================================================
// TAILWIND CONFIG EXPORTS (for tailwind.config.js compatibility)
// ============================================================================

/**
 * Brand colors in the legacy format for tailwind.config.js
 * This format matches the old brand-colors.ts structure
 */
interface LegacyBrandColor {
	name: string
	backgroundColor: string
	lightBackgroundColor: string
	accentColor?: string
	defaultStyle?: string
	collapsed?: string
	selected?: string
}

/**
 * Get all colors in legacy format for Tailwind config
 * Used by tailwind.config.js to generate color classes
 */
function getBrandColorsForTailwind(): Record<string, LegacyBrandColor> {
	const result: Record<string, LegacyBrandColor> = {}

	for (const [id, color] of Object.entries(colorRegistry)) {
		result[id] = {
			name: color.name,
			backgroundColor: color.hexCode,
			lightBackgroundColor: color.lightHexCode || 'default',
			defaultStyle: color.customStyles?.defaultStyle,
			collapsed: color.customStyles?.collapsed,
			selected: color.customStyles?.selected,
		}
	}

	return result
}

/**
 * Get colors formatted for Kontent MAC (model-as-code) files
 * Returns array of codename/name objects
 */
function getBrandColorsForMAC(): KontentColorOption[] {
	return getColorsForCMS()
}

// Export the registry colors in legacy format for backward compatibility
export const brandColorsFromRegistry = getBrandColorsForTailwind()
const brandColorsMacFromRegistry = getBrandColorsForMAC()

// ============================================================================
// CONSOLIDATED EXPORTS (Single Source of Truth)
// These replace the old brand-colors.ts, general-colors.ts, tailwind-colors.ts
// ============================================================================

/**
 * Brand colors for model-as-code files
 * Replaces: import \{ brandColorsMac \} from './brand-colors'
 */
export const brandColorsMac: KontentColorOption[] = brandColorsMacFromRegistry

/**
 * Tailwind colors - for components that check available color keys
 * Replaces: import \{ tailwindColors \} from './tailwind-colors'
 */
export const tailwindColors: Record<string, {DEFAULT: string}> =
	Object.fromEntries(
		Object.entries(colorRegistry).map(([id, color]) => [
			id,
			{DEFAULT: color.hexCode},
		])
	)

// ============================================================================
// HEX CODE VALIDATION
// ============================================================================

/**
 * Validate a hex code format
 * @param hex - The hex code to validate (with or without #)
 * @returns true if valid hex code format
 */
export function isValidHexCode(hex: string): boolean {
	const cleanHex = hex.startsWith('#') ? hex.slice(1) : hex
	return /^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(cleanHex)
}

interface HexValidationError {
	id: string
	hexCode: string
	error: string
}

/**
 * Validate all hex codes in the registry
 * Use this in tests or at build time to catch invalid colors
 * @returns Array of invalid color entries (empty if all valid)
 */
export function validateRegistryHexCodes(): HexValidationError[] {
	const errors: HexValidationError[] = []

	for (const [id, color] of Object.entries(colorRegistry)) {
		if (!isValidHexCode(color.hexCode)) {
			errors.push({
				id,
				hexCode: color.hexCode,
				error: `Invalid hex code format: ${color.hexCode}`,
			})
		}
		// Also validate lightHexCode if present
		if (color.lightHexCode && !isValidHexCode(color.lightHexCode)) {
			errors.push({
				id,
				hexCode: color.lightHexCode,
				error: `Invalid lightHexCode format: ${color.lightHexCode}`,
			})
		}
	}

	return errors
}

/**
 * Safe hex code getter with fallback
 * Returns fallback color if the input is invalid
 */
export function getSafeHexCode(hexCode: string, fallback = '#0066CC'): string {
	if (isValidHexCode(hexCode)) {
		return hexCode.startsWith('#') ? hexCode : `#${hexCode}`
	}
	return fallback
}
